import React from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Plus, Trash2, CalendarDays } from 'lucide-react';

/**
 * OccurrencesEditor — admin editor for multi-day events.
 * Each occurrence has its own date/times/label/restriction/capacity. Tickets
 * (configured separately under Tickets tab) can be tied to a specific
 * occurrence_id or apply to all days.
 */
export default function OccurrencesEditor({ occurrences, onChange }) {
  const list = occurrences || [];

  const addRow = () => {
    onChange([
      ...list,
      {
        occurrence_id: `occ_new_${Date.now()}`,
        date: '',
        doors_open: '',
        start_time: '',
        end_time: '',
        label: '',
        restriction: '',
        capacity: '',
        is_active: true,
      },
    ]);
  };

  const updateRow = (i, patch) => {
    onChange(list.map((r, ri) => (ri === i ? { ...r, ...patch } : r)));
  };

  const removeRow = (i) => {
    onChange(list.filter((_, ri) => ri !== i));
  };

  return (
    <div className="space-y-4" data-testid="occurrences-editor">
      <div className="rounded-lg border border-indigo-500/30 bg-indigo-500/5 p-3 text-[11px] text-indigo-300 leading-relaxed">
        <strong>Multi-day event?</strong> Add an occurrence for each day. Each can have its own start/end times, sub-title (e.g. "Ladies-Only Night"), restriction, and capacity. Under the Tickets tab, you can target a ticket type to a specific day (or leave it valid for all days).
        {list.length === 0 && <> Leave empty for a single-day event — the main Date/Time fields under <strong>Basic Info</strong> are used.</>}
      </div>

      {list.length === 0 ? (
        <div className="text-center py-8 border border-dashed border-border rounded-lg">
          <CalendarDays className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground mb-3">No additional occurrences yet.</p>
          <Button type="button" variant="outline" size="sm" onClick={addRow} data-testid="add-first-occurrence">
            <Plus className="h-4 w-4 mr-1" /> Add first day
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((occ, i) => (
            <Card key={occ.occurrence_id || i} className="border-border" data-testid={`occurrence-card-${i}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Day {i + 1}</div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-rose-400 hover:text-rose-300 h-7 w-7"
                    onClick={() => removeRow(i)}
                    data-testid={`remove-occurrence-${i}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Date *</Label>
                    <Input
                      type="date"
                      value={occ.date || ''}
                      onChange={(e) => updateRow(i, { date: e.target.value })}
                      data-testid={`occ-date-${i}`}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Sub-title / theme</Label>
                    <Input
                      value={occ.label || ''}
                      onChange={(e) => updateRow(i, { label: e.target.value })}
                      placeholder="e.g. Ladies Only Night"
                      data-testid={`occ-label-${i}`}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Doors open</Label>
                    <Input
                      type="time"
                      value={occ.doors_open || ''}
                      onChange={(e) => updateRow(i, { doors_open: e.target.value })}
                      data-testid={`occ-doors-${i}`}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Start time</Label>
                    <Input
                      type="time"
                      value={occ.start_time || ''}
                      onChange={(e) => updateRow(i, { start_time: e.target.value })}
                      data-testid={`occ-start-${i}`}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">End time</Label>
                    <Input
                      type="time"
                      value={occ.end_time || ''}
                      onChange={(e) => updateRow(i, { end_time: e.target.value })}
                      data-testid={`occ-end-${i}`}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Restriction (optional)</Label>
                    <Input
                      value={occ.restriction || ''}
                      onChange={(e) => updateRow(i, { restriction: e.target.value })}
                      placeholder="e.g. Ladies-only 21+"
                      data-testid={`occ-restriction-${i}`}
                    />
                  </div>
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label className="text-xs">Capacity for this day (optional)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={occ.capacity || ''}
                      onChange={(e) => updateRow(i, { capacity: e.target.value ? parseInt(e.target.value, 10) : null })}
                      placeholder="Leave blank to inherit event capacity"
                      data-testid={`occ-capacity-${i}`}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          <Button type="button" variant="outline" size="sm" onClick={addRow} data-testid="add-occurrence">
            <Plus className="h-4 w-4 mr-1" /> Add another day
          </Button>
        </div>
      )}
    </div>
  );
}
